import { getAnalyticsClient } from './client'

// Helper to track events — silently drops events when analytics is not initialized
// (expected on backend services that don't have PostHog configured)
let hasWarnedNotInitialized = false
const AnalyticsProvider = {
  track: (eventName: string, properties: any) => {
    try {
      getAnalyticsClient().track({ event_name: eventName, properties })
    } catch {
      if (!hasWarnedNotInitialized) {
        console.warn(
          '[Analytics] Client not initialized — events will be silently dropped. Call initializeAnalytics() to enable tracking.'
        )
        hasWarnedNotInitialized = true
      }
    }
  },
}

/**
 * Domain-Driven Tracking Functions
 *
 * This file contains tracking functions organized by business domain.
 * Each function is strongly typed and maps to specific PostHog/Amplitude events.
 */

export const trackUserLogin = (properties: {
  user_id: string
  user_type: 'admin' | 'staff' | 'client' | 'corporate'
  role: string
  site_id: string
  login_method: 'email' | 'sso' | 'mobile'
}) => AnalyticsProvider.track('user_login', properties)

export const trackMemberCheckIn = (properties: {
  member_id: string
  site_id: string
  check_in_method: 'qr' | 'nfc' | 'manual' | 'app'
  class_id?: string
  instructor_id?: string
}) => AnalyticsProvider.track('member_check_in', properties)

export const trackClassBooked = (properties: {
  class_id: string
  site_id: string
  member_id: string
  class_type: string
  instructor_id: string
  spots_remaining: number
  booking_method: 'app' | 'web' | 'front_desk'
}) => AnalyticsProvider.track('class_booked', properties)

export const trackClassCanceled = (properties: {
  class_id: string
  member_id: string
  attendance_rate: number
}) => AnalyticsProvider.track('class_canceled', properties)

export const trackSchedulePublished = (properties: {
  site_id: string
  created_by: string
  week_of: string
  staff_count: number
}) => AnalyticsProvider.track('schedule_published', properties)

export const trackStaffShiftAssigned = (properties: {
  staff_id: string
  site_id: string
  shift_date: string
  shift_hours: number
}) => AnalyticsProvider.track('staff_shift_assigned', properties)

// Corporate & Onboarding Events
export const trackCorporateSignup = (properties: {
  corporate_id: string
  site_id: string
  source: 'sales' | 'self_service' | 'partner'
}) => {
  AnalyticsProvider.track('corporate_signup', properties)
}

export const trackOnboardingStepCompleted = (properties: {
  corporate_id: string
  step: 'site_setup' | 'staff_added' | 'schedule_created' | 'payment_setup'
  time_to_complete_minutes: number
}) => {
  AnalyticsProvider.track('onboarding_step_completed', properties)
}

export const trackOnboardingCompleted = (properties: {
  corporate_id: string
  site_id: string
  total_duration_days: number
  completed_steps: string[]
}) => {
  AnalyticsProvider.track('onboarding_completed', properties)
}

export const trackFeatureAdopted = (properties: {
  user_id: string
  module: 'scheduling' | 'payments' | 'reports' | 'marketing' | 'crm'
  site_id: string
}) => AnalyticsProvider.track('feature_adopted', properties)

export const trackFeatureEngagement = (properties: {
  user_id: string
  modules_used: string[]
  usage_frequency: 'daily' | 'weekly' | 'monthly'
}) => AnalyticsProvider.track('feature_engagement_tracked', properties)

// Revenue & Subscription Events
export const trackSubscriptionStarted = (properties: {
  site_id: string
  plan_type: 'basic' | 'premium' | 'unlimited'
  mrr_amount: number
  payment_method: string
}) => {
  AnalyticsProvider.track('subscription_started', properties)
}

export const trackSubscriptionUpgraded = (properties: {
  site_id: string
  previous_plan: string
  new_plan: string
  mrr_change: number
  change_reason: string
}) => {
  AnalyticsProvider.track('subscription_upgraded', properties)
}

export const trackNpsResponse = (properties: {
  user_id: string
  score: number
  feedback?: string
  context: 'post_class' | 'monthly_survey' | 'support_ticket'
}) => {
  AnalyticsProvider.track('nps_response', properties)
}

export const trackOnboardingImproved = (properties: {
  corporate_id: string
  previous_onboarding_days: number
  current_onboarding_days: number
  improvement_percentage: number
  hubspot_deal_value: number
}) => {
  AnalyticsProvider.track('onboarding_improved', properties)
}

export const trackPaymentFailed = (properties: {
  member_id: string
  site_id: string
  error_code: string
  error_message: string
  retry_count: number
}) => AnalyticsProvider.track('payment_failed', properties)

export const trackPageView = (properties: {
  page_name: string
  duration_ms: number
  user_id: string
  device_type: 'mobile' | 'desktop' | 'tablet'
}) => AnalyticsProvider.track('page_viewed', properties)

export const trackApiError = (properties: {
  endpoint: string
  status_code: number
  error_message: string
  user_id: string
  retry_attempted: boolean
}) => AnalyticsProvider.track('api_error_occurred', properties)

export const trackBookingAbandoned = (properties: {
  user_id: string
  class_id: string
  abandonment_stage: 'class_selection' | 'time_selection' | 'payment'
  time_on_page_seconds: number
}) => AnalyticsProvider.track('booking_abandoned', properties)

// Churn & At-Risk Indicators
export const trackInactiveUser = (properties: {
  user_id: string
  days_since_last_visit: number
  user_type: string
}) => AnalyticsProvider.track('inactive_user_identified', properties)

export const trackChurnRiskDetected = (properties: {
  site_id: string
  risk_factors: string[]
  risk_score: number
  last_activity_days_ago: number
}) => {
  AnalyticsProvider.track('churn_risk_detected', properties)
}

export const trackEngagementDeclined = (properties: {
  site_id: string
  previous_monthly_campaigns: number
  current_monthly_campaigns: number
  decline_percentage: number
}) => {
  AnalyticsProvider.track('engagement_declined', properties)
}

// ============================================================================
// PLG Trial & Onboarding Events
// ============================================================================

export const trackTrialStarted = (properties: {
  corporate_id: string
  duration_days: number
  lead_source: string
  business_type?: string
}) => AnalyticsProvider.track('trial_started', properties)

export const trackTrialExpiring = (properties: { corporate_id: string; days_remaining: number }) =>
  AnalyticsProvider.track('trial_expiring', properties)

export const trackTrialExpired = (properties: {
  corporate_id: string
  onboarding_progress_pct: number
  steps_completed: number
}) => AnalyticsProvider.track('trial_expired', properties)

export const trackTrialConverted = (properties: {
  corporate_id: string
  days_to_convert: number
  onboarding_progress_pct: number
  plan_id?: string
}) => AnalyticsProvider.track('trial_converted', properties)

export const trackTrialExtended = (properties: {
  corporate_id: string
  additional_days: number
  extended_by?: string
}) => AnalyticsProvider.track('trial_extended', properties)

export const trackTrialClosed = (properties: {
  corporate_id: string
  onboarding_progress_pct: number
  steps_completed: number
}) => AnalyticsProvider.track('trial_closed', properties)

export const trackOnboardingStepViewed = (properties: { corporate_id: string; step_key: string }) =>
  AnalyticsProvider.track('onboarding_step_viewed', properties)

export const trackOnboardingStepSkipped = (properties: {
  corporate_id: string
  step_key: string
}) => AnalyticsProvider.track('onboarding_step_skipped', properties)

export const trackOnboardingAbandoned = (properties: {
  corporate_id: string
  last_step: string
  progress_pct: number
  days_since_last_activity: number
}) => AnalyticsProvider.track('onboarding_abandoned', properties)

export const trackWelcomeScreenViewed = (properties: { corporate_id: string }) =>
  AnalyticsProvider.track('welcome_screen_viewed', properties)

export const trackWelcomeScreenDismissed = (properties: {
  corporate_id: string
  action: 'skip' | 'get_started' | 'quick_action'
}) => AnalyticsProvider.track('welcome_screen_dismissed', properties)

export const trackTrialBannerViewed = (properties: {
  corporate_id: string
  days_remaining: number
}) => AnalyticsProvider.track('trial_banner_viewed', properties)

export const trackTrialBannerCtaClicked = (properties: {
  corporate_id: string
  cta: 'contact_sales' | 'upgrade'
}) => AnalyticsProvider.track('trial_banner_cta_clicked', properties)

// PLG Staff & Location Events

export const trackStaffInvited = (properties: {
  corporate_id: string
  site_id?: string
  role: string
  invited_by: string
}) => AnalyticsProvider.track('staff_invited', properties)

export const trackStaffInvitationAccepted = (properties: {
  corporate_id: string
  invitation_id: string
  days_to_accept: number
}) => AnalyticsProvider.track('staff_invitation_accepted', properties)

export const trackStaffBulkInvite = (properties: {
  corporate_id: string
  count: number
  roles: string[]
}) => AnalyticsProvider.track('staff_bulk_invite', properties)

export const trackLocationWizardStarted = (properties: { corporate_id: string }) =>
  AnalyticsProvider.track('location_wizard_started', properties)

export const trackLocationWizardCompleted = (properties: {
  corporate_id: string
  site_id: string
  rooms_count: number
  has_payment_gateway: boolean
}) => AnalyticsProvider.track('location_wizard_completed', properties)

// PLG Engagement Events

export const trackEmptyStateViewed = (properties: { page: string; user_role?: string }) =>
  AnalyticsProvider.track('empty_state_viewed', properties)

export const trackEmptyStateCtaClicked = (properties: { page: string; action: string }) =>
  AnalyticsProvider.track('empty_state_cta_clicked', properties)

export const trackNudgeShown = (properties: {
  nudge_id: string
  type: 'spotlight' | 'banner' | 'toast'
  page: string
}) => AnalyticsProvider.track('nudge_shown', properties)

export const trackNudgeDismissed = (properties: { nudge_id: string; type: string }) =>
  AnalyticsProvider.track('nudge_dismissed', properties)

export const trackFeatureFirstUse = (properties: {
  feature_name: string
  days_since_signup: number
}) => AnalyticsProvider.track('feature_first_use', properties)

// PLG Import Events

export const trackDataImportStarted = (properties: { corporate_id: string; source_type: string }) =>
  AnalyticsProvider.track('data_import_started', properties)

export const trackDataImportCompleted = (properties: {
  corporate_id: string
  source_type: string
  records_imported: number
  errors: number
}) => AnalyticsProvider.track('data_import_completed', properties)

// PLG Conversion Events

export const trackUpgradePageViewed = (properties: {
  corporate_id: string
  days_remaining: number
}) => AnalyticsProvider.track('upgrade_page_viewed', properties)

export const trackUpgradeInitiated = (properties: { corporate_id: string; plan_id: string }) =>
  AnalyticsProvider.track('upgrade_initiated', properties)

export const trackUpgradeCompleted = (properties: {
  corporate_id: string
  plan_id: string
  mrr: number
}) => AnalyticsProvider.track('upgrade_completed', properties)

export const trackTrialExpiredWallShown = (properties: { corporate_id: string }) =>
  AnalyticsProvider.track('trial_expired_wall_shown', properties)

// Support & Issue Resolution
export const trackSupportTicketCreated = (properties: {
  ticket_id: string
  category: string
  priority: string
  subject: string
  source: 'email' | 'chat' | 'happyfox'
  created_at: string
}) => {
  AnalyticsProvider.track('support_ticket_created', properties)
}

export const trackSupportTicketResolved = (properties: {
  ticket_id: string
  resolution_time_hours: number
  satisfaction_rating?: number
  agent_id: string
  source: 'email' | 'chat' | 'happyfox'
}) => {
  AnalyticsProvider.track('support_ticket_resolved', properties)
}

export const trackSupportSatisfactionRated = (properties: {
  rating: number
  ticket_id: string
  category: string
  resolution_time_hours?: number
}) => {
  AnalyticsProvider.track('support_satisfaction_rated', properties)
}

export const trackSupportTicketEscalated = (properties: {
  ticket_id: string
  escalation_reason: string
  time_to_escalation_hours: number
}) => {
  AnalyticsProvider.track('support_ticket_escalated', properties)
}

export const trackSupportFirstResponse = (properties: {
  ticket_id: string
  response_time_minutes: number
  agent_id: string
}) => {
  AnalyticsProvider.track('support_first_response_tracked', properties)
}
